import { useCallback, useRef } from 'react';
import { UpgradeWebModal } from 'design-system/UpgradeWebModal';
import { Tier } from 'features/premium/entities';
import { setTransactionsRequesting } from 'actions';
import { useAppDispatch, useAppStore } from 'store/hooks';
import { isCreditReportAccount } from 'utils/creditReportAccounts';
import Links from 'utils/links';
import { selectFeatureFlag } from 'reducers/selectors';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { useTabNavigation } from 'hooks/navigation/useTabNavigation';
import useMonevoEntry from 'features/borrow/monevo/hooks/useMonevoEntry';
import { useAccountMenu } from 'features/accounts/hooks/useAccountMenu';
import AddAccountModal from 'features/accounts/components/AddAccountModal';
import useCreditReportDebtTrackingFlow from 'features/credit-score/hooks/useCreditReportDebtTrackingFlow';
import { selectCreditReportAccountByNetworthAccountId, selectUntrackedCreditReportDebts, } from 'features/credit-score/selectors';
import { isWeb } from '../constants';
import usePlaidLink from './usePlaidLink';
import { useOpenConnectionLogin } from './useOpenConnectionLogin';
const useAccountCallbacks = (navigateBackAfterDelete = true) => {
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const openMonevo = useMonevoEntry();
    const { navigateToTab } = useTabNavigation();
    const store = useAppStore();
    const { offerTrackingInsteadOfOffline } = useCreditReportDebtTrackingFlow();
    const openPlaidTapTime = useRef(undefined);
    const onOpenUpgrade = useCallback((data) => {
        const tier = data.ultimatePreviewAccount ? Tier.ultimate : Tier.pro;
        if (isWeb) {
            UpgradeWebModal.show(tier);
            return;
        }
        if (data.deactivatedOverQuota) {
            navigation.navigateDeprecated('LoginsUpgrade', { fromConnectionId: data.bankConnectionId });
        }
        else {
            navigation.navigateDeprecated('EmmaProModal', {
                tier,
                isTierRequired: true,
            });
        }
    }, []);
    const onRowLongPress = useAccountMenu(navigation, navigateBackAfterDelete);
    const onRowPress = useCallback((data, touchViewRef) => {
        if (isCreditReportAccount(data) && !data.proPreviewAccount) {
            const reportAccount = selectCreditReportAccountByNetworthAccountId(store.getState(), data.id);
            if (reportAccount?.accountId != null) {
                navigation.navigate('CreditReportAccountDetailScreen', { accountId: reportAccount.accountId });
            }
            else {
                navigation.navigate('CreditReportAccountsScreen');
            }
        }
        else if (!data.ultimatePreviewAccount && !data.proPreviewAccount && !data.deactivatedOverQuota) {
            dispatch(setTransactionsRequesting);
            navigation.navigateDeprecated('Transactions', {
                id: data.id,
            });
        }
        else {
            onOpenUpgrade(data, touchViewRef);
        }
    }, [onOpenUpgrade, store]);
    const login = useOpenConnectionLogin(navigation);
    const onOpenLogins = useCallback((data) => {
        login({ type: 'account', data });
    }, [login]);
    const openPlaid = usePlaidLink();
    const onAddAccount = useCallback((type) => {
        if (type === 'LOAN' || type === 'MORTGAGE') {
            offerTrackingInsteadOfOffline(selectUntrackedCreditReportDebts(store.getState()), () => navigation.navigateDeprecated('DummyAccountSetup', { type }));
        }
        else if (type === 'OTHER' || type === 'VEHICLE' || type === 'REAL_ESTATE') {
            navigation.navigateDeprecated('DummyAccountSetup', { type });
        }
        else {
            AddAccountModal.show({
                navigate: navigation.navigateDeprecated,
                onOpenPlaid: () => {
                    const now = new Date().getTime();
                    if (!openPlaidTapTime.current || now - openPlaidTapTime.current > 1000) {
                        openPlaidTapTime.current = now;
                        openPlaid();
                    }
                },
                onAddOffline: () => {
                    navigation.navigateDeprecated('PickAccountType', {});
                },
                onAddProperty: () => {
                    navigation.navigateDeprecated('Property', {});
                },
            });
        }
    }, [offerTrackingInsteadOfOffline, openPlaid]);
    const openAlternative = useCallback((type) => {
        if (type === 'LOAN') {
            openMonevo('LOAN');
        }
        else if (type === 'MORTGAGE') {
            const mortgagesUrl = selectFeatureFlag(store.getState(), 'mortgages_habito').extra?.url;
            if (mortgagesUrl) {
                Links.openLink(mortgagesUrl);
            }
            else {
                Links.openKnownLink('mortgagesHabito');
            }
        }
        else if (type === 'SAVINGS') {
            navigateToTab('Save', { tab: 'pots' });
        }
        else if (type === 'INVESTMENT') {
            navigateToTab('Invest');
        }
    }, [openMonevo, store]);
    return {
        onRowPress,
        onRowLongPress,
        onOpenLogins,
        onOpenUpgrade,
        onAddAccount,
        openAlternative,
    };
};
export default useAccountCallbacks;
